import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BasicsState, Cadence, ResourceType } from "@/lib/plan-editor";

/**
 * Phase 25 — Plan Editor Basics section.
 *
 * Dumb controlled component. Parent (PlanEditorPage) owns BasicsState
 * via PlanEditorState aggregator and threads value+onChange.
 *
 * 11 fields ported VERBATIM from the retired popup editor (deleted in
 * Plan 25-06) into a single section component.
 *
 * Covers: ACCORD25-01 field surface (Basics).
 * Wave 2 — markup + wiring ONLY. No save logic (Plan 25-04).
 * No progressive lock (Plan 25-04).
 */

const CADENCES = ["Recurring", "Non_Recurring"] as const;
const RESOURCE_TYPES = [
  "Workstation",
  "Private_Cabin",
  "Meeting_Room",
  "Conference_Room",
] as const;
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  Workstation: "Workstation",
  Private_Cabin: "Private Cabin",
  Meeting_Room: "Meeting Room",
  Conference_Room: "Conference Room",
};

export interface BasicsSectionProps {
  value: BasicsState;
  onChange: (next: BasicsState) => void;
  /**
   * When true, the page is pre-scoped to a space via `?spaceId=N`.
   * The section renders a small hint near the top. There is no space
   * picker in Basics (Plans were always per-space in the Sheet).
   */
  spaceLocked: boolean;
  /** Plan 25-04 — Save & Continue button click handler. */
  onSave: () => void | Promise<void>;
  /** Plan 25-04 — true while any section save is in flight. */
  saving: boolean;
  /**
   * 2026-06-09 — when true, all inputs lock and the footer shows an Edit
   * button instead of Save & Continue. Page sets this true once the
   * section has been saved at least once AND it's NOT currently being
   * edited.
   */
  disabled?: boolean;
  /** 2026-06-09 — Edit button click handler. */
  onEdit?: () => void;
}

export function BasicsSection({
  value,
  onChange,
  spaceLocked,
  onSave,
  saving,
  disabled = false,
  onEdit,
}: BasicsSectionProps) {
  const isRecurring = value.cadence === "Recurring";

  function patch(next: Partial<BasicsState>) {
    onChange({ ...value, ...next });
  }

  // Mirror canUnlockPricing required-set: planName + cadence + monthlyPrice > 0.
  // Resource Type is NOT required — "no space hold" is a valid configuration.
  // Section button stays disabled until these pass.
  const monthlyNum = parseFloat(value.monthlyPrice);
  const isSectionValid =
    value.planName.trim().length > 0 &&
    value.cadence !== "" &&
    Number.isFinite(monthlyNum) &&
    monthlyNum > 0;

  return (
    <div className="space-y-4 p-1" data-testid="plan-editor-basics-section">
    <fieldset disabled={disabled} className="space-y-4 disabled:opacity-70">
      {spaceLocked && value.spaceId !== null && (
        <p className="text-xs text-muted-foreground">
          Pre-scoped to Space #{value.spaceId}. Change via URL.
        </p>
      )}

      <div className="space-y-1.5">
        <Label>
          Plan Name <span className="text-destructive">*</span>
        </Label>
        <Input
          value={value.planName}
          onChange={(e) => patch({ planName: e.target.value })}
          placeholder="e.g. Hot_Desk, Day_Pass, Workstation"
        />
        <p className="text-[10px] text-muted-foreground">
          Use_Underscores_For_Multi_Word — convention used elsewhere in the system.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>
            Cadence <span className="text-destructive">*</span>
          </Label>
          <Select
            value={value.cadence || ""}
            onValueChange={(v) => patch({ cadence: v as Cadence })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select cadence" />
            </SelectTrigger>
            <SelectContent>
              {CADENCES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c === "Recurring" ? "Recurring" : "Non-Recurring"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Resource Type</Label>
          <Select
            value={value.resourceType || "none"}
            onValueChange={(v) =>
              patch({ resourceType: (v === "none" ? "" : v) as ResourceType })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— (no space hold)</SelectItem>
              {RESOURCE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {RESOURCE_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>
          {isRecurring ? "Monthly Price (₹)" : "Pass Price (₹)"}{" "}
          <span className="text-destructive">*</span>
        </Label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={value.monthlyPrice}
          onChange={(e) => patch({ monthlyPrice: e.target.value })}
          placeholder="e.g. 11999"
          className="max-w-[200px]"
        />
      </div>

      {/* Phase 26 (VALIDITY26-01): Validity (days) input REMOVED from Basics.
          Operators now set validity via the Pricing section — baseline row
          reads `validityDays` from the hydrated plan, and operator-added rows
          carry their own per-row validity. `value.validityDays` is kept on
          BasicsState as a data carrier so the Pricing baseline row can read
          it from props. */}

      <div className="space-y-1.5">
        <Label>Minimum Commitment ({isRecurring ? "months" : "days"})</Label>
        <Input
          type="number"
          min={1}
          value={value.minimumCommitmentMonths}
          onChange={(e) => patch({ minimumCommitmentMonths: e.target.value })}
          placeholder={isRecurring ? "e.g. 6" : "e.g. 1"}
          className="max-w-[200px]"
        />
        <p className="text-[10px] text-muted-foreground">
          Informational only — surfaced as a warning in Convert Lead.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select
            value={value.status}
            onValueChange={(v) => patch({ status: v as "Active" | "Archived" })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col justify-end space-y-1">
          <Label>Show on Website</Label>
          <div className="flex items-center gap-2 h-9">
            <Switch
              checked={value.showOnWebsite}
              onCheckedChange={(v) => patch({ showOnWebsite: v })}
            />
            <span className="text-sm text-muted-foreground">
              {value.showOnWebsite ? "Visible" : "Hidden"}
            </span>
          </div>
        </div>
      </div>

      {/* 0059 — Plan manager additions: setupFees + hsnCode */}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <div className="space-y-1">
          <Label>Setup Fees (₹)</Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={value.setupFees}
            onChange={(e) => patch({ setupFees: e.target.value })}
            placeholder="0"
          />
          <p className="text-[10px] text-muted-foreground">
            One-time onboarding charge. Default 0.
          </p>
        </div>

        <div className="space-y-1">
          <Label>HSN Code</Label>
          <Input
            value={value.hsnCode}
            onChange={(e) => patch({ hsnCode: e.target.value })}
            placeholder="e.g. 9973"
          />
          <p className="text-[10px] text-muted-foreground">
            Manage codes under Inventory → HSN &amp; GST. GST resolves from this
            code at billing time.
          </p>
        </div>
      </div>

      <div className="rounded-md border p-3">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-sm">Customer Approval Required</Label>
            <p className="text-xs text-muted-foreground">
              {value.requiresApproval
                ? "Customers must submit a request; operator approves before access."
                : "Customers can self-avail (immediate)."}
            </p>
          </div>
          <Switch
            checked={value.requiresApproval}
            onCheckedChange={(v) => patch({ requiresApproval: v })}
          />
        </div>
      </div>

      </fieldset>
      <div className="flex items-center justify-end gap-3 pt-2">
        {disabled ? (
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            data-testid="plan-editor-basics-edit"
          >
            Edit
          </Button>
        ) : (
          <>
            {!isSectionValid && (
              <p className="text-xs text-muted-foreground">
                Fill Plan Name, Cadence, and Price &gt; 0 to continue.
              </p>
            )}
            {isSectionValid && (
              <Button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="plan-editor-basics-save"
              >
                {saving ? "Saving…" : "Save & Continue"}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
